import { combineReducers } from "@reduxjs/toolkit";

import type { BookmarksAction } from "domains/bookmarks/actionTypes";
import { BookmarkActionTypes } from "domains/bookmarks/actionTypes";
import type { PinAddRejectAction } from "domains/pinAddReject/actionTypes";
import { PinAddRejectActionTypes } from "domains/pinAddReject/actionTypes";
import type { PinAddRejectState } from "domains/pinAddReject/types";
import { getKnowledgeType } from "entities/knowledge/reducers";
import type { Knowledge } from "entities/knowledge/types";

const initialState: PinAddRejectState = {
  pinnedKnowledge: { data: [], currentPage: 1, limit: 0, totalCount: 0, isPending: false, hasNextPage: false },
  pinnedKnowledgeUpdateStatus: {},
  knowledgeRejectionStatus: {},
  addedKnowledgeStatus: {
    isAddKnowledgePending: false,
    requestError: null,
  },
  approveKnowledgeStatus: {
    isApproveKnowledgePending: false,
  },
  perKnowledge: {},
};

const pinnedKnowledge = (
  state = initialState.pinnedKnowledge,
  action: PinAddRejectAction | BookmarksAction,
): PinAddRejectState["pinnedKnowledge"] => {
  switch (action.type) {
    case PinAddRejectActionTypes.FETCH_PINNED_KNOWLEDGE_FULFILLED:
      return {
        ...state,
        currentTopicId: action.meta.topicId,
        data: ((data: Knowledge[]) => (action.meta.page === 1 || !action.meta.limit ? data : [...state.data, ...data]))(
          action.payload.data.map((item) => ({ ...item, knowledgeType: getKnowledgeType(item) })),
        ),
        totalCount: action.meta.page === 1 ? action.payload.data.length : state.totalCount + action.payload.data.length,
        currentPage: action.meta.page ?? initialState.pinnedKnowledge.currentPage,
        limit: action.meta.limit ?? initialState.pinnedKnowledge.limit,
        isPending: false,
        hasNextPage: action.meta.limit ? action.payload.data.length >= action.meta.limit : true,
      };
    case PinAddRejectActionTypes.FETCH_PINNED_KNOWLEDGE:
    case PinAddRejectActionTypes.FETCH_PINNED_KNOWLEDGE_REJECTED:
      return { ...state, isPending: true };
    case PinAddRejectActionTypes.UPDATE_PINNED_KNOWLEDGE_FULFILLED: {
      if (!action.meta.isPinned) {
        const dataWithoutUnpinnedKnowledge = state.data.filter((item) => item.id !== action.meta.knowledgeId);
        return {
          ...state,
          data: dataWithoutUnpinnedKnowledge.sort((a, b) => b.time - a.time),
          totalCount: state.totalCount - 1,
        };
      }

      const newData = action.payload.updatedKnowledge
        ? [...state.data, { ...action.payload.updatedKnowledge, is_pinned: true }]
        : state.data;

      return {
        ...state,
        data: newData.sort((a, b) => b.time - a.time),
        totalCount: state.totalCount + 1,
      };
    }
    case BookmarkActionTypes.BOOKMARK_TOGGLE: {
      const bookmarkedKnowledge = state.data.find((item) => item.id === action.meta.bookmark.id);

      if (!bookmarkedKnowledge) return state;

      const pendingBookmarkedKnowledge: Knowledge = {
        ...bookmarkedKnowledge,
        isBookmarkPending: true,
      };

      return {
        ...state,
        data: state.data.map((item) => (item.id === action.meta.bookmark.id ? pendingBookmarkedKnowledge : item)),
      };
    }
    case BookmarkActionTypes.BOOKMARK_TOGGLE_FULFILLED: {
      const bookmarkedKnowledge = state.data.find((item) => item.id === action.meta.bookmark.id);

      if (!bookmarkedKnowledge) return state;

      const updatedBookmarkedKnowledge: Knowledge = {
        ...bookmarkedKnowledge,
        isBookmarkPending: false,
        is_bookmarked: action.meta.isBookmarked,
      };

      return {
        ...state,
        data: state.data.map((item) => (item.id === action.meta.bookmark.id ? updatedBookmarkedKnowledge : item)),
      };
    }
    default:
      return state;
  }
};

const pinnedKnowledgeUpdateStatus = (
  state = initialState.pinnedKnowledgeUpdateStatus,
  action: PinAddRejectAction,
): PinAddRejectState["pinnedKnowledgeUpdateStatus"] => {
  switch (action.type) {
    case PinAddRejectActionTypes.UPDATE_PINNED_KNOWLEDGE:
      return { ...state, [action.meta.knowledgeId]: { isPending: true } };

    case PinAddRejectActionTypes.UPDATE_PINNED_KNOWLEDGE_FULFILLED:
    case PinAddRejectActionTypes.UPDATE_PINNED_KNOWLEDGE_REJECTED:
      return { ...state, [action.meta.knowledgeId]: { isPending: false } };
    default:
      return state;
  }
};

const knowledgeRejectionStatus = (
  state = initialState.knowledgeRejectionStatus,
  action: PinAddRejectAction,
): PinAddRejectState["knowledgeRejectionStatus"] => {
  switch (action.type) {
    case PinAddRejectActionTypes.REJECT_KNOWLEDGE_FULFILLED:
      return {
        ...state,
        [action.meta.knowledgeId]: { isPending: false, isSuccess: true, isFailure: false },
      };
    case PinAddRejectActionTypes.REJECT_KNOWLEDGE:
      return {
        ...state,
        [action.meta.knowledgeId]: { isPending: true, isSuccess: false, isFailure: false },
      };
    case PinAddRejectActionTypes.REJECT_KNOWLEDGE_REJECTED:
      return {
        ...state,
        [action.meta.knowledgeId]: { isPending: false, isSuccess: false, isFailure: true },
      };
    default:
      return state;
  }
};

const addedKnowledgeStatus = (
  state = initialState.addedKnowledgeStatus,
  action: PinAddRejectAction,
): PinAddRejectState["addedKnowledgeStatus"] => {
  switch (action.type) {
    case PinAddRejectActionTypes.ADD_KNOWLEDGE:
      return { ...state, isAddKnowledgePending: true, requestError: null };
    case PinAddRejectActionTypes.ADD_KNOWLEDGE_FULFILLED:
      return {
        ...state,
        newKnowledgeId: action.payload.newKnowledgeId,
        isAddKnowledgePending: false,
        requestError: null,
      };
    case PinAddRejectActionTypes.ADD_KNOWLEDGE_REJECTED:
      return { ...state, requestError: { status: action.payload.errorStatus }, isAddKnowledgePending: false };
    case PinAddRejectActionTypes.RESET_ADDED_KNOWLEDGE:
      return initialState.addedKnowledgeStatus;
    default:
      return state;
  }
};

const approveKnowledgeStatus = (
  state = initialState.approveKnowledgeStatus,
  action: PinAddRejectAction,
): PinAddRejectState["approveKnowledgeStatus"] => {
  switch (action.type) {
    case PinAddRejectActionTypes.APPROVE_KNOWLEDGE:
      return { ...state, isApproveKnowledgePending: true };
    case PinAddRejectActionTypes.APPROVE_KNOWLEDGE_FULFILLED:
    case PinAddRejectActionTypes.APPROVE_KNOWLEDGE_REJECTED:
      return { ...state, isApproveKnowledgePending: false };
    default:
      return state;
  }
};

const perKnowledge = (
  state = initialState.perKnowledge,
  action: PinAddRejectAction,
): PinAddRejectState["perKnowledge"] => {
  switch (action.type) {
    case PinAddRejectActionTypes.FETCH_TOPICS_FOR_PINNED_KNOWLEDGE:
      return {
        ...state,
        [action.meta.knowledgeId]: {
          topicIds: [],
          areTopicsPending: true,
        },
      };
    case PinAddRejectActionTypes.FETCH_TOPICS_FOR_PINNED_KNOWLEDGE_FULFILLED:
      return {
        ...state,
        [action.meta.knowledgeId]: {
          topicIds: action.payload.topicIds,
          areTopicsPending: false,
        },
      };
    case PinAddRejectActionTypes.FETCH_TOPICS_FOR_PINNED_KNOWLEDGE_REJECTED:
      return {
        ...state,
        [action.meta.knowledgeId]: {
          topicIds: [],
          areTopicsPending: false,
        },
      };
    default:
      return state;
  }
};

export const pinAddRejectReducer = combineReducers({
  pinnedKnowledge,
  pinnedKnowledgeUpdateStatus,
  knowledgeRejectionStatus,
  addedKnowledgeStatus,
  approveKnowledgeStatus,
  perKnowledge,
});
